import { useEffect, useState, type FormEvent } from "react";
import { accountMessage } from "../account/messages";
import { SheetOverlay } from "./SheetOverlay";
import type { FriendsActions, FriendsView } from "./view-model";

export interface FriendsSheetProps {
  friends?: FriendsView;
  actions?: FriendsActions;
  onOpenAccount(): void;
  onClose(): void;
}

export function FriendsSheet({
  friends,
  actions,
  onOpenAccount,
  onClose,
}: FriendsSheetProps) {
  const [localProblem, setLocalProblem] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [newCodeConfirm, setNewCodeConfirm] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [removingFriendId, setRemovingFriendId] = useState<string | null>(null);

  const closeInnerView = (): boolean => {
    if (newCodeConfirm) {
      setNewCodeConfirm(false);
      return true;
    }
    if (removingFriendId !== null) {
      setRemovingFriendId(null);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (friends?.signedIn) {
      void actions?.onLoadFriends();
    }
  }, [friends?.signedIn, actions]);

  const effectiveProblem = localProblem ?? friends?.problem ?? null;

  const handleCopyCode = async () => {
    if (!friends?.profile) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(friends.profile.code.replace(/-/g, ""));
      }
    } catch {
      // ignore clipboard error
    }
    setCopiedCode(true);
    window.setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyLink = async () => {
    if (!friends?.profile) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(friends.profile.shareLink);
      }
    } catch {
      // ignore clipboard error
    }
    setCopiedLink(true);
    window.setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleAddFriend = async (e: FormEvent) => {
    e.preventDefault();
    if (!addCode.trim() || !actions || friends?.busy) return;
    setLocalProblem(null);
    const code = await actions.onAddFriend(addCode.trim());
    if (code) {
      setLocalProblem(code);
    } else {
      setAddCode("");
      setLocalProblem(null);
    }
  };

  const profile = friends?.profile;

  return (
    <SheetOverlay
      testId="friends-sheet"
      title="Friends"
      onClose={onClose}
      onEscape={closeInnerView}
    >
      <div className="sb">
        {!friends || !actions ? (
          <div className="note">Friends are not available right now.</div>
        ) : !friends.signedIn ? (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "10px" }}>
            <div className="note">
              Sign in to add friends. Playing alone never needs an account.
            </div>
            <button
              className="btn"
              type="button"
              data-testid="friends-signin"
              onClick={onOpenAccount}
            >
              Sign in
            </button>
          </div>
        ) : !friends.loaded ? (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "10px" }}>
            <div data-testid="friends-loading">Loading…</div>
            {effectiveProblem ? (
              <div className="note" role="alert" data-testid="friends-problem">
                {accountMessage(effectiveProblem)}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {effectiveProblem ? (
              <div className="note" role="alert" data-testid="friends-problem">
                {accountMessage(effectiveProblem)}
              </div>
            ) : null}

            {friends.pendingCode ? (
              <div className="row col" data-testid="friends-pending" style={{ gap: "8px" }}>
                <div className="grow">
                  {`Add the friend with code ${friends.pendingCode}?`}
                </div>
                <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                  <button
                    className="btn"
                    type="button"
                    data-testid="friends-pending-add"
                    disabled={friends.busy}
                    onClick={async () => {
                      if (!friends.pendingCode) return;
                      const code = await actions.onAddFriend(friends.pendingCode);
                      if (code) {
                        setLocalProblem(code);
                      } else {
                        setLocalProblem(null);
                      }
                    }}
                  >
                    Add
                  </button>
                  <button
                    className="btn alt"
                    type="button"
                    data-testid="friends-pending-dismiss"
                    disabled={friends.busy}
                    onClick={() => actions.onDismissPendingCode()}
                  >
                    Not now
                  </button>
                </div>
              </div>
            ) : null}

            {profile ? (
              <>
                <h4>You</h4>
                <div className="row">
                  <label htmlFor="friends-name" className="grow">Name</label>
                  <div className="fi">
                    <select
                      id="friends-name"
                      data-testid="friends-name"
                      value={profile.nameIndex}
                      disabled={friends.busy}
                      onChange={(e) => actions.onSetProfile(Number(e.target.value), profile.avatarIndex)}
                    >
                      {friends.names.map((nm, idx) => (
                        <option key={idx} value={idx}>
                          {nm}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="grow">
                    <span
                      className={`avatar-badge avatar-badge-${profile.avatarIndex % 8}`}
                      aria-hidden="true"
                    >
                      {(friends.avatars[profile.avatarIndex] ?? "").charAt(0).toUpperCase()}
                    </span>
                    <label htmlFor="friends-avatar">Avatar</label>
                  </div>
                  <div className="fi">
                    <select
                      id="friends-avatar"
                      data-testid="friends-avatar"
                      value={profile.avatarIndex}
                      disabled={friends.busy}
                      onChange={(e) => actions.onSetProfile(profile.nameIndex, Number(e.target.value))}
                    >
                      {friends.avatars.map((av, idx) => (
                        <option key={idx} value={idx}>
                          {av}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row col" style={{ alignItems: "flex-start", gap: "8px" }}>
                  <div>
                    <small style={{ color: "#9fb6c8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Your code
                    </small>
                    <div
                      className="digs"
                      data-testid="friends-code"
                      style={{
                        fontFamily: "var(--pix)",
                        fontSize: "calc(16px * var(--fs, 1))",
                        letterSpacing: "1px",
                        marginTop: "4px",
                      }}
                    >
                      {profile.code}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", width: "100%" }}>
                    <button
                      className="btn alt"
                      type="button"
                      data-testid="friends-copy"
                      onClick={handleCopyCode}
                    >
                      {copiedCode ? "Copied" : "Copy code"}
                    </button>
                    <button
                      className="btn alt"
                      type="button"
                      data-testid="friends-copy-link"
                      onClick={handleCopyLink}
                    >
                      {copiedLink ? "Copied" : "Copy link"}
                    </button>
                    {!newCodeConfirm ? (
                      <button
                        className="btn alt"
                        type="button"
                        data-testid="friends-newcode"
                        disabled={friends.busy}
                        onClick={() => setNewCodeConfirm(true)}
                      >
                        New code
                      </button>
                    ) : null}
                  </div>
                  {newCodeConfirm ? (
                    <div style={{ width: "100%", marginTop: "4px" }}>
                      <div className="note">
                        Your old code stops working. Friends you already have stay.
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                        <button
                          className="btn"
                          type="button"
                          data-testid="friends-newcode-confirm"
                          disabled={friends.busy}
                          onClick={async () => {
                            setNewCodeConfirm(false);
                            await actions.onNewCode();
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          className="btn alt"
                          type="button"
                          data-testid="friends-newcode-cancel"
                          onClick={() => setNewCodeConfirm(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="row">
                  <div className="grow">
                    Let friends see my picture book
                    <small>
                      Off by default. Friends see the animals, scores and habitats, never the names you typed. You can turn it off any time.
                    </small>
                  </div>
                  <button
                    className="sw"
                    type="button"
                    role="switch"
                    aria-checked={profile.bookShared}
                    aria-label="Let friends see my picture book"
                    data-testid="friends-share"
                    disabled={friends.busy}
                    onClick={() => actions.onSetBookShared(!profile.bookShared)}
                  />
                </div>
              </>
            ) : null}

            <form
              onSubmit={handleAddFriend}
              style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}
            >
              <label htmlFor="friends-add-input">Friend code</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <div className="fi" style={{ flex: 1 }}>
                  <input
                    id="friends-add-input"
                    data-testid="friends-add-input"
                    autoComplete="off"
                    maxLength={16}
                    autoCapitalize="characters"
                    value={addCode}
                    disabled={friends.busy}
                    onChange={(e) => setAddCode(e.target.value)}
                  />
                </div>
                <button
                  className="btn"
                  type="submit"
                  data-testid="friends-add"
                  disabled={friends.busy || !addCode.trim()}
                >
                  Add
                </button>
              </div>
            </form>

            <h4>Friends</h4>
            {friends.friends.length === 0 ? (
              <div className="note" data-testid="friends-empty">
                No friends yet. Share your code or enter a friend's.
              </div>
            ) : (
              friends.friends.map((friend) => (
                <div className="row col" key={friend.id} data-testid="friends-row" style={{ gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <span
                      className={`avatar-badge avatar-badge-${friend.avatarIndex % 8}`}
                      aria-hidden="true"
                    >
                      {friend.avatarLabel.charAt(0).toUpperCase()}
                    </span>
                    <div className="grow">
                      <div>{friend.name}</div>
                      <small>
                        {friend.bookShared
                          ? `Picture book shared (${friend.bookCount})`
                          : "Picture book private"}
                      </small>
                    </div>
                    {removingFriendId !== friend.id ? (
                      <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                        {friend.bookShared ? (
                          <button
                            className="btn alt"
                            type="button"
                            data-testid="friends-open-book"
                            disabled={friends.busy}
                            onClick={() => actions.onOpenFriendBook(friend.id)}
                          >
                            See book
                          </button>
                        ) : null}
                        <button
                          className="btn alt"
                          type="button"
                          data-testid="friends-remove"
                          disabled={friends.busy}
                          onClick={() => setRemovingFriendId(friend.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {removingFriendId === friend.id ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        flexWrap: "wrap",
                        paddingTop: "4px",
                      }}
                    >
                      <span className="note" style={{ flex: 1, minWidth: "120px" }}>
                        {`Remove ${friend.name}?`}
                      </span>
                      <button
                        className="btn"
                        type="button"
                        data-testid="friends-remove-confirm"
                        disabled={friends.busy}
                        onClick={async () => {
                          setRemovingFriendId(null);
                          await actions.onRemoveFriend(friend.id);
                        }}
                      >
                        Remove
                      </button>
                      <button
                        className="btn alt"
                        type="button"
                        data-testid="friends-remove-cancel"
                        onClick={() => setRemovingFriendId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </SheetOverlay>
  );
}
